import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { analyzeAPI, userAPI } from "../api/api";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Badge, StatusBadge, Alert, Spinner, PageHeader } from "../components/UI";
import { Upload, Image, CheckCircle, AlertCircle, Eye, RotateCcw } from "lucide-react";

// ── Result Card ───────────────────────────────────────────────
function ResultCard({ result, imagePreview }) {
  const isHealthy = result.status?.toLowerCase() === "healthy";
  const isSick = result.status?.toLowerCase() === "sick";
  const isReview = !isHealthy && !isSick;

  const bgColor = isHealthy ? "bg-brand-50 border-brand-300" : isSick ? "bg-red-50 border-red-300" : "bg-earth-50 border-earth-300";
  const headerColor = isHealthy ? "bg-brand-600" : isSick ? "bg-red-500" : "bg-earth-500";
  const emoji = isHealthy ? "✅" : isSick ? "🚨" : "🔍";

  return (
    <div className={`rounded-2xl border-2 overflow-hidden shadow-md animate-slide-up ${bgColor}`}>
      {/* Status header */}
      <div className={`${headerColor} text-white px-5 py-4 flex items-center gap-3`}>
        <span className="text-3xl">{emoji}</span>
        <div>
          <div className="font-900 text-xl">{result.status}</div>
          <div className="text-sm opacity-90 font-600">{result.diagnosis}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-2xl font-900">{result.confidence}%</div>
          <div className="text-xs opacity-80 font-600">Confidence</div>
        </div>
      </div>

      <div className="p-5 grid md:grid-cols-2 gap-5">
        {/* Image preview */}
        {imagePreview && (
          <div className="rounded-xl overflow-hidden border-2 border-white shadow-sm">
            <img src={imagePreview} alt="Analyzed" className="w-full h-48 object-cover" />
            <div className="bg-white px-3 py-2 text-xs font-700 text-brand-600 flex items-center gap-1">
              <Eye size={12} /> Analyzed Image
            </div>
          </div>
        )}

        {/* Details */}
        <div className="flex flex-col gap-3">
          <div>
            <div className="text-xs font-800 text-gray-400 uppercase tracking-wider mb-1">What We Found</div>
            <p className="text-sm font-600 text-gray-700 leading-relaxed">{result.description}</p>
          </div>

          {result.treatment && (
            <div className="bg-white rounded-xl p-3 border border-gray-200">
              <div className="text-xs font-800 text-gray-400 uppercase tracking-wider mb-1"> Treatment</div>
              <p className="text-sm font-700 text-gray-800 leading-relaxed">{result.treatment}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-white rounded-lg p-2.5 border border-gray-200">
              <div className="text-gray-400 font-700 mb-0.5">Severity</div>
              <div className="font-800 text-gray-800">{result.severity || "—"}</div>
            </div>
            <div className="bg-white rounded-lg p-2.5 border border-gray-200">
              <div className="text-gray-400 font-700 mb-0.5">AI Model</div>
              <div className="font-800 text-gray-800">{result.model_version || "v2.1"}</div>
            </div>
          </div>

          <div className="text-xs text-gray-400 font-600">
            📍 {result.species_detected} · ID: {result.id?.slice(-8)}
          </div>
        </div>
      </div>

      {isSick && (
        <div className="mx-5 mb-5 bg-red-100 border border-red-200 rounded-xl p-3 flex items-start gap-2">
          <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm font-700 text-red-700">
            ⚠️ Your plant/animal needs attention. Please follow the treatment advice above or visit your nearest agricultural officer.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Confidence Bar ────────────────────────────────────────────
function ConfidenceBar({ value }) {
  const color = value >= 80 ? "bg-brand-500" : value >= 60 ? "bg-earth-500" : "bg-red-400";
  return (
    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
      <div className={`h-2 rounded-full transition-all duration-700 ${color}`} style={{ width: `${value}%` }} />
    </div>
  );
}

// ── Upload Zone ───────────────────────────────────────────────
function UploadZone({ onFile }) {
  const [dragOver, setDragOver] = useState(false);

  const onDrop = useCallback((accepted) => {
    setDragOver(false);
    if (accepted.length) onFile(accepted[0]);
  }, [onFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    onDragEnter: () => setDragOver(true),
    onDragLeave: () => setDragOver(false),
  });

  return (
    <div
      {...getRootProps()}
      className={`border-3 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${
        isDragActive || dragOver
          ? "border-brand-500 bg-brand-50 scale-[1.01]"
          : "border-brand-200 bg-white hover:border-brand-400 hover:bg-brand-50"
      }`}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center gap-4">
        <div className={`w-20 h-20 rounded-2xl flex items-center justify-center transition-all ${isDragActive ? "bg-brand-200 scale-110" : "bg-brand-100"}`}>
          <span className="text-5xl">{isDragActive ? "📥" : "📸"}</span>
        </div>
        <div>
          <p className="text-xl font-900 text-brand-800">{isDragActive ? "Drop it here!" : "Upload a Photo"}</p>
          <p className="text-brand-500 font-600 mt-1">Take a photo of your plant or animal</p>
          <p className="text-xs text-brand-400 font-600 mt-2">Supports JPG and PNG • Max 10MB</p>
        </div>
        {!isDragActive && (
          <div className="flex gap-3 text-sm">
            <span className="bg-brand-100 text-brand-700 px-3 py-1.5 rounded-lg font-700">📱 Take Photo</span>
            <span className="bg-brand-100 text-brand-700 px-3 py-1.5 rounded-lg font-700">🖼️ Choose File</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Stats row ─────────────────────────────────────────────────
function StatBadge({ emoji, label, value, color = "brand" }) {
  const colors = { brand: "bg-brand-50 border-brand-200 text-brand-700", red: "bg-red-50 border-red-200 text-red-700", earth: "bg-earth-50 border-earth-200 text-earth-700" };
  return (
    <div className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 ${colors[color]}`}>
      <span className="text-2xl">{emoji}</span>
      <div>
        <div className="text-lg font-900 leading-none">{value}</div>
        <div className="text-xs font-700 opacity-70 mt-0.5">{label}</div>
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────
export default function DashboardPage() {
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState(null);

  // Quick stats from history (loaded lazily)
  React.useEffect(() => {
    userAPI.getHistory()
      .then((r) => setHistory(r.data.results || []))
      .catch(() => {});
  }, [result]);

  const handleFile = (file) => {
    setSelectedFile(file);
    setResult(null);
    setError("");
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await analyzeAPI.upload(selectedFile);
      setResult(res.data);
    } catch (err) {
      // setError(err?.response?.data?.message || "Analysis failed. Please try again or check your connection.");
      if (err?.response?.status === 429) {
        setError("Too many requests. Please try again after 24 hours.");
      } else {
        setError(err?.response?.data?.message || "Analysis failed. Please try again or check your connection.");
      }

    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSelectedFile(null);
    setPreview(null);
    setResult(null);
    setError("");
  };

  const totalAnalyses = history?.length || 0;
  const sickCount = history?.filter((h) => h.status?.toLowerCase() === "sick").length || 0;
  const healthyCount = history?.filter((h) => h.status?.toLowerCase() === "healthy").length || 0;

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title={`Hello, ${user?.name?.split(" ")[0] || "Farmer"}! 👋`}
        subtitle="Upload a photo to check plant or animal health"
      />

      {/* Quick stats */}
      {history !== null && totalAnalyses > 0 && (
        <div className="flex flex-wrap gap-3 mb-6">
          <StatBadge emoji="🔬" label="Total Analyses" value={totalAnalyses} />
          <StatBadge emoji="✅" label="Healthy" value={healthyCount} color="brand" />
          <StatBadge emoji="🚨" label="Sick Found" value={sickCount} color="red" />
        </div>
      )}

      {/* How to use - shown for new users */}
      {history !== null && totalAnalyses === 0 && (
        <Card className="p-5 mb-6 bg-gradient-to-r from-brand-50 to-earth-50">
          <h3 className="font-800 text-brand-800 mb-3 text-lg">🌾 How to use AgriHealth</h3>
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            {[
              { step: "1", emoji: "📸", text: "Take a clear photo of your plant or animal" },
              { step: "2", emoji: "⬆️", text: "Upload it using the button below" },
              { step: "3", emoji: "🔬", text: "Get instant AI diagnosis and treatment advice" },
            ].map((s) => (
              <div key={s.step} className="flex items-start gap-2.5 bg-white rounded-xl p-3 border border-brand-100">
                <div className="w-7 h-7 bg-brand-600 text-white rounded-lg flex items-center justify-center font-800 text-sm flex-shrink-0">{s.step}</div>
                <div>
                  <span className="text-lg">{s.emoji}</span>
                  <p className="text-brand-700 font-600 mt-0.5 leading-snug">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Upload Area */}
      <Card className="p-5 mb-5">
        {!selectedFile ? (
          <UploadZone onFile={handleFile} />
        ) : (
          <div className="space-y-4">
            {/* Preview */}
            <div className="relative rounded-xl overflow-hidden bg-brand-50 border-2 border-brand-200">
              <img src={preview} alt="Preview" className="w-full max-h-64 object-contain" />
              <div className="absolute top-2 right-2">
                <button onClick={reset} className="bg-white rounded-xl px-3 py-1.5 text-sm font-700 text-brand-600 shadow-md hover:bg-brand-50 flex items-center gap-1.5 border border-brand-200">
                  <RotateCcw size={14} /> Change Photo
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-brand-50 rounded-xl border border-brand-200">
              <span className="text-2xl">📄</span>
              <div className="flex-1 min-w-0">
                <div className="font-800 text-brand-800 text-sm truncate">{selectedFile.name}</div>
                <div className="text-xs text-brand-400 font-600">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</div>
              </div>
              <CheckCircle size={20} className="text-brand-500 flex-shrink-0" />
            </div>

            {error && <Alert type="error" onDismiss={() => setError("")}>{error}</Alert>}

            <Button onClick={handleAnalyze} loading={loading} size="lg" className="w-full text-lg">
              {loading ? "🔬 Analyzing your photo..." : "🚀 Analyze Now — Get Results!"}
            </Button>

            {loading && (
              <div className="text-center py-4">
                <div className="text-5xl animate-bounce mb-2">🤖</div>
                <p className="text-brand-600 font-700">Our AI is checking your photo...</p>
                <p className="text-brand-400 font-600 text-sm mt-1">This usually takes 2-3 seconds</p>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Results */}
      {result && (
        <div className="mb-6">
          <h2 className="text-xl font-900 text-brand-800 mb-3 flex items-center gap-2">
            <span>🔬</span> Analysis Result
          </h2>
          <ResultCard result={result} imagePreview={preview} />
          <div className="mt-4 flex gap-3">
            <Button onClick={reset} variant="secondary" className="flex-1">
              📸 Analyze Another Photo
            </Button>
          </div>
        </div>
      )}

      {/* Recent history preview */}
      {history && history.length > 0 && !result && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-800 text-brand-700 text-base">📋 Recent Analyses</h3>
            <a href="/history" className="text-sm font-700 text-brand-500 hover:text-brand-700">View All →</a>
          </div>
          <div className="flex flex-col gap-2">
            {history.slice(0, 3).map((h) => (
              <div key={h.id} className="flex items-center gap-3 p-3 rounded-xl bg-brand-50 border border-brand-100">
                <span className="text-xl">{h.status?.toLowerCase() === "healthy" ? "✅" : h.status?.toLowerCase() === "sick" ? "🚨" : "🔍"}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-700 text-brand-800 text-sm truncate">{h.diagnosis}</div>
                  <div className="text-xs text-brand-400 font-600">{new Date(h.analyzed_at).toLocaleDateString()}</div>
                </div>
                <StatusBadge status={h.status} />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
